import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package } from 'lucide-react';
import zoneService from '../services/zoneService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';

const ZoneDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [zone, setZone] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchZoneDetail();
  }, [id]);

  const fetchZoneDetail = async () => {
    try {
      setLoading(true);
      const response = await zoneService.getZone(id);
      if (response.success) {
        setZone(response.data.zone);
        setProducts(response.data.products);
      } else {
        setError('Failed to load zone details');
      }
    } catch (err) {
      console.error('Error fetching zone:', err);
      setError('Failed to load zone details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <Header />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-5 sm:py-8">
        {/* Header with back button */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/zones')}
            className="p-2 rounded-xl bg-white/80 backdrop-blur-xl shadow-sm border border-white/30 hover:bg-white transition-all"
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          {zone && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center text-2xl">
                {zone.icon || '📦'}
              </div>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-gray-800">{zone.name}</h1>
                {zone.description && (
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{zone.description}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500 text-sm">Loading products...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="text-4xl mb-2">⚠️</div>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-1">Something went wrong</h2>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchZoneDetail}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:shadow-md transition-all text-sm"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            {/* Product count */}
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <Package size={16} />
              <span>{products.length} {products.length === 1 ? 'Product' : 'Products'} in this zone</span>
            </div>

            {/* Products grid */}
            {products.length === 0 ? (
              <div className="text-center py-16 bg-white/60 backdrop-blur-xl rounded-2xl border border-white/30">
                <div className="text-4xl mb-2">📭</div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1">No products yet</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Products in this zone are being added. Check back soon!
                </p>
                <button
                  onClick={() => navigate('/store')}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:shadow-md transition-all text-sm"
                >
                  Browse All Products
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
                {products.map((product) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ZoneDetail;

